import { listFolderFiles } from './drive-client.js';
/**
 * Tree-aware cache of folder + file ids under a single root folder.
 *
 * Paths use `/` separators relative to the root, e.g.
 *   'studies'                            (top-level folder)
 *   'studies/Genesis 1 Study'            (nested record folder)
 *   'studies/Genesis 1 Study/study.json' (file)
 */
export class DriveIndex {
    rootId;
    idByPath = new Map();
    listedFolders = new Set();
    /** path → array of immediate-child names, computed at list time. */
    childrenByFolder = new Map();
    constructor(rootId) {
        this.rootId = rootId;
        this.idByPath.set('', rootId);
    }
    /** Returns the id at the given path, listing folders along the way as needed. */
    async resolve(token, path) {
        if (this.idByPath.has(path))
            return this.idByPath.get(path) ?? null;
        const segments = path.split('/').filter(Boolean);
        let current = '';
        for (const seg of segments) {
            const parentId = this.idByPath.get(current);
            if (!parentId)
                return null;
            await this.ensureListed(token, current);
            const childPath = current ? `${current}/${seg}` : seg;
            if (!this.idByPath.has(childPath))
                return null;
            current = childPath;
        }
        return this.idByPath.get(current) ?? null;
    }
    /** Cache an id at a path (used after creating a new file/folder). */
    set(path, id) {
        this.idByPath.set(path, id);
        const parent = parentPath(path);
        const name = path.slice(parent ? parent.length + 1 : 0);
        const children = this.childrenByFolder.get(parent) ?? [];
        if (!children.includes(name)) {
            children.push(name);
            this.childrenByFolder.set(parent, children);
        }
    }
    /** Forget a path (called after delete). */
    remove(path) {
        this.idByPath.delete(path);
        const parent = parentPath(path);
        const name = path.slice(parent ? parent.length + 1 : 0);
        const children = this.childrenByFolder.get(parent);
        if (children) {
            this.childrenByFolder.set(parent, children.filter((n) => n !== name));
        }
    }
    /** List the immediate children (names) of the folder at the given path. */
    async children(token, folderPath) {
        await this.ensureListed(token, folderPath);
        return this.childrenByFolder.get(folderPath) ?? [];
    }
    clear() {
        this.idByPath.clear();
        this.idByPath.set('', this.rootId);
        this.listedFolders.clear();
        this.childrenByFolder.clear();
    }
    async ensureListed(token, folderPath) {
        if (this.listedFolders.has(folderPath))
            return;
        const folderId = this.idByPath.get(folderPath);
        if (!folderId) {
            this.listedFolders.add(folderPath);
            this.childrenByFolder.set(folderPath, []);
            return;
        }
        const files = await listFolderFiles(token, folderId);
        const names = [];
        for (const f of files) {
            if (!f.name)
                continue;
            const childPath = folderPath ? `${folderPath}/${f.name}` : f.name;
            this.idByPath.set(childPath, f.id);
            names.push(f.name);
        }
        this.childrenByFolder.set(folderPath, names);
        this.listedFolders.add(folderPath);
    }
}
function parentPath(path) {
    const i = path.lastIndexOf('/');
    return i < 0 ? '' : path.slice(0, i);
}
//# sourceMappingURL=drive-index.js.map